/**
 * ThreadListItem — uma linha da inbox de mensageria interna (Item #8b, Slice C).
 *
 * Direct: avatar redondo (iniciais) + nome da contraparte.
 * Group:  ícone de time (quadrado) + rótulo genérico honesto "Canal do time".
 *
 * Honest-defer (R15): nem o nome do time nem o rótulo rico da âncora estão no
 * contrato do thread — usamos rótulos genéricos (não fabricamos "#28934 · Plano
 * Família" nem nome de time). Ver `thread-display.ts`.
 */

import { Link2, Users } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

import { avatarColorFor, initialsOf } from '../../../lib/format';
import { relativeTime } from '../../conversations/format';
import { directCounterpartId, listThreadTitle, unreadDisplay } from '../thread-display';

import type { ThreadSummary } from '../types';

interface ThreadListItemProps {
  thread: ThreadSummary;
  currentUserId: string | null;
  selected: boolean;
}

export function ThreadListItem({
  thread,
  currentUserId,
  selected,
}: ThreadListItemProps): JSX.Element {
  const navigate = useNavigate();
  const isGroup = thread.kind === 'group';
  const hasUnread = thread.unread > 0;

  const counterpartId = directCounterpartId(thread, currentUserId);
  // Nome da contraparte resolvido no backend (via User.name); não depende de presença.
  const title = listThreadTitle(thread, currentUserId);

  const avatarKey = counterpartId ?? thread.threadId;
  const initials = isGroup ? '' : initialsOf(thread.counterpartName ?? counterpartId ?? '');

  const preview = thread.lastMessagePreview ?? 'Sem mensagens ainda';
  const groupTag = `Canal · ${thread.participantUserIds.length.toString()}`;

  const className = ['dm-thread', selected ? 'dm-thread--selected' : ''].filter(Boolean).join(' ');

  return (
    <button
      type="button"
      className={className}
      aria-current={selected ? 'true' : undefined}
      onClick={() => {
        navigate(`/mensagens/${thread.threadId}`);
      }}
    >
      {isGroup ? (
        <span aria-hidden className="dm-thread__avatar dm-thread__avatar--group">
          <Users size={18} strokeWidth={1.75} />
        </span>
      ) : (
        <span
          aria-hidden
          className="dm-thread__avatar dm-thread__avatar--direct"
          style={{ background: avatarColorFor(avatarKey) }}
        >
          {initials}
        </span>
      )}

      <span className="dm-thread__main">
        <span className="dm-thread__row">
          <span
            className={['dm-thread__name', hasUnread ? 'dm-thread__name--unread' : '']
              .filter(Boolean)
              .join(' ')}
          >
            {title}
          </span>
          <span className="dm-thread__time">{relativeTime(thread.lastMessageAt)}</span>
        </span>

        <span className="dm-thread__preview-row">
          <span className="dm-thread__preview">{preview}</span>
          {hasUnread && (
            <span
              className="dm-thread__unread-pill"
              aria-label={`${thread.unread.toString()} não lidas`}
            >
              {unreadDisplay(thread.unread)}
            </span>
          )}
        </span>

        {(isGroup || thread.anchorConversationId !== null) && (
          <span className="dm-thread__chips">
            {isGroup && <span className="dm-chip dm-chip--group">{groupTag}</span>}
            {thread.anchorConversationId !== null && (
              // Chip genérico clicável: o contrato só dá o id da conversa.
              // Rótulo rico da âncora é honest-defer (não está no contrato).
              <Link
                to={`/atendimento/conversation/${thread.anchorConversationId}`}
                className="dm-chip dm-chip--anchor"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <Link2 size={11} strokeWidth={1.75} />
                Sobre uma conversa
              </Link>
            )}
          </span>
        )}
      </span>
    </button>
  );
}
